import { useEffect, useMemo, useState } from 'react'
import { Button } from '../ui-kitNew/buttonNew'
import { Field, Input, Segmented, Switch } from '../ui-kitNew/controlsNew'
import { Modal } from '../ui-kitNew/modalNew'
import { Select } from '../ui-kitNew/selectNew'
import {
  AreaChartIcon,
  BarChartIcon,
  DonutIcon,
  GaugeIcon,
  LineChartIcon,
  PieChartIcon,
  StackedBarIcon,
  TableIcon,
} from '../ui-kitNew/iconsNew'
import { DIMENSION_FIELDS, METRIC_FIELDS, groupFields } from '../../lib/reportNew/fieldsNew'
import { AGGREGATIONS, DATE_GRANULARITIES, aggregationMeta } from '../../lib/reportNew/aggregateNew'
import {
  CHART_TYPES,
  HEIGHT_OPTIONS,
  SORT_OPTIONS,
  SPAN_OPTIONS,
  adaptWidgetToChartType,
  chartTypeMeta,
} from '../../lib/reportNew/widgetsNew'
import { getField } from '../../lib/reportNew/fieldsNew'
import { FilterBuilder } from '../filtersNew/filter-builderNew'

const CHART_ICONS = {
  kpi: <GaugeIcon size={16} />,
  bar: <BarChartIcon size={16} />,
  barStacked: <StackedBarIcon size={16} />,
  barHorizontal: <BarChartIcon size={16} className="rotate-90" />,
  line: <LineChartIcon size={16} />,
  area: <AreaChartIcon size={16} />,
  donut: <DonutIcon size={16} />,
  pie: <PieChartIcon size={16} />,
  ranking: <TableIcon size={16} />,
}

/** Eje temporal por defecto al pasar a un gráfico de evolución. */
const DEFAULT_DATE_FIELD =
  DIMENSION_FIELDS.find((field) => field.type === 'date')?.key || DIMENSION_FIELDS[0].key

const fieldOptions = (fields) =>
  groupFields(fields).flatMap((group) =>
    group.fields.map((field) => ({ value: field.key, label: field.label, group: group.name }))
  )

/**
 * Editor de un gráfico: tipo, dimensión, métrica, orden, tamaño y filtros
 * propios. Todas las opciones se derivan de los metadatos del dataset.
 */
export function WidgetEditor({ open, widget, rows, onClose, onSave }) {
  const [draft, setDraft] = useState(widget)
  const [tab, setTab] = useState('datos')

  useEffect(() => {
    if (open) {
      setDraft(widget)
      setTab('datos')
    }
  }, [open, widget])

  const meta = chartTypeMeta(draft?.chartType)
  const dimensionField = getField(draft?.dimension)
  const aggMeta = aggregationMeta(draft?.metric?.agg)

  const dimensionOptions = useMemo(() => fieldOptions(DIMENSION_FIELDS), [])
  const metricOptions = useMemo(() => fieldOptions(METRIC_FIELDS), [])

  if (!open || !draft) return null

  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }))
  const setMetric = (patch) => set({ metric: { ...draft.metric, ...patch } })
  const setSecondMetric = (patch) =>
    set({ secondMetric: { ...(draft.secondMetric || { agg: 'sum', field: 'peso' }), ...patch } })

  const isKpi = draft.chartType === 'kpi'
  const hasSecondMetric = Boolean(draft.secondMetric)
  const hasBreakdown = Boolean(draft.breakdown)

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={widget?.__isNew ? 'Añadir gráfico' : 'Editar gráfico'}
      description="Define qué se mide, cómo se agrupa y cómo se representa."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => onSave(draft)}>
            {widget?.__isNew ? 'Añadir al informe' : 'Guardar cambios'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Segmented
          size="md"
          ariaLabel="Secciones del editor"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'datos', label: 'Datos' },
            { value: 'formato', label: 'Formato' },
            { value: 'filtros', label: `Filtros propios${draft.filters?.conditions?.length ? ` (${draft.filters.conditions.length})` : ''}` },
          ]}
        />

        {tab === 'datos' && (
          <div className="flex flex-col gap-5">
            <Field label="Tipo de visualización">
              <div className="grid gap-2 sm:grid-cols-3">
                {CHART_TYPES.map((type) => {
                  const active = type.value === draft.chartType
                  return (
                    <button
                      key={type.value}
                      type="button"
                      aria-pressed={active}
                          onClick={() =>
                            setDraft((prev) =>
                              adaptWidgetToChartType(prev, type.value, {
                                dateFieldKey: DEFAULT_DATE_FIELD,
                              })
                            )
                      }
                      className={`flex items-start gap-2.5 rounded-xl border p-2.5 text-left transition-colors ${
                        active
                          ? 'border-primary bg-primary-soft'
                          : 'border-border bg-card hover:border-ring/60 hover:bg-muted'
                      }`}
                    >
                      <span className={active ? 'text-primary' : 'text-muted-foreground'}>
                        {CHART_ICONS[type.value]}
                      </span>
                      <span className="min-w-0">
                        <span
                          className={`block text-[13px] font-semibold ${active ? 'text-primary' : 'text-foreground'}`}
                        >
                          {type.label}
                        </span>
                        <span className="block text-[12px] leading-snug text-muted-foreground">
                          {type.hint}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Métrica" hint="Qué se calcula en cada grupo.">
                <Select
                  options={AGGREGATIONS.map((agg) => ({ value: agg.value, label: agg.label }))}
                  value={draft.metric.agg}
                  onChange={(agg) => setMetric({ agg })}
                  ariaLabel="Tipo de agregación"
                />
              </Field>
              <Field label="Campo de la métrica">
                <Select
                  searchable
                  disabled={!aggMeta.needsField}
                  options={
                    draft.metric.agg === 'countDistinct'
                      ? fieldOptions(DIMENSION_FIELDS.concat(METRIC_FIELDS))
                      : metricOptions
                  }
                  value={draft.metric.field}
                  onChange={(field) => setMetric({ field })}
                  placeholder={aggMeta.needsField ? 'Elige un campo…' : 'No aplica'}
                  ariaLabel="Campo de la métrica"
                />
              </Field>
            </div>

            {!isKpi && (
              <Switch
                checked={hasSecondMetric}
                onChange={(checked) =>
                  checked
                    ? set({ secondMetric: { agg: 'sum', field: 'peso' }, breakdown: '', dimension: '' })
                    : set({ secondMetric: null })
                }
                label="Segunda métrica (comparar dos valores en el mismo gráfico)"
              />
            )}

            {hasSecondMetric && (
              <div className="grid gap-4 sm:grid-cols-2 rounded-lg border border-dashed border-primary/30 bg-primary-soft/30 p-3">
                <Field label="Agregación (2ª métrica)">
                  <Select
                    options={AGGREGATIONS.map((agg) => ({ value: agg.value, label: agg.label }))}
                    value={draft.secondMetric?.agg || 'sum'}
                    onChange={(agg) => setSecondMetric({ agg })}
                    ariaLabel="Tipo de agregación segunda métrica"
                  />
                </Field>
                <Field label="Campo (2ª métrica)">
                  <Select
                    searchable
                    options={
                      draft.secondMetric?.agg === 'countDistinct'
                        ? fieldOptions(DIMENSION_FIELDS.concat(METRIC_FIELDS))
                        : metricOptions
                    }
                    value={draft.secondMetric?.field || 'peso'}
                    onChange={(field) => setSecondMetric({ field })}
                    placeholder="Elige un campo…"
                    ariaLabel="Campo de la segunda métrica"
                  />
                </Field>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={isKpi ? 'Desglose del indicador (opcional)' : 'Agrupar por (eje)'}
                hint={isKpi ? 'Muestra el reparto bajo el valor principal.' : undefined}
              >
                <Select
                  searchable
                  options={
                    isKpi
                      ? [{ value: '', label: 'Sin desglose' }, ...dimensionOptions]
                      : hasSecondMetric
                        ? [{ value: '', label: 'Sin dimensión (total global)' }, ...dimensionOptions]
                        : dimensionOptions
                  }
                  value={draft.dimension}
                  onChange={(dimension) => set({ dimension })}
                  placeholder="Elige un campo…"
                  ariaLabel="Campo de agrupación"
                />
              </Field>

              {dimensionField?.type === 'date' ? (
                <Field label="Granularidad temporal">
                  <Select
                    options={DATE_GRANULARITIES}
                    value={draft.dateGranularity}
                    onChange={(dateGranularity) => set({ dateGranularity })}
                    ariaLabel="Granularidad temporal"
                  />
                </Field>
              ) : (
                meta.allowsBreakdown && !hasSecondMetric && (
                  <Field label="Serie / desglose (opcional)" hint="Divide cada grupo en varias series.">
                    <Select
                      searchable
                      options={[{ value: '', label: 'Sin desglose' }, ...dimensionOptions]}
                      value={draft.breakdown}
                      onChange={(breakdown) => set({ breakdown, ...(breakdown ? { secondMetric: null } : {}) })}
                      ariaLabel="Campo de desglose"
                    />
                  </Field>
                )
              )}
            </div>

            {dimensionField?.type === 'date' && meta.allowsBreakdown && !hasSecondMetric && (
              <Field label="Serie / desglose (opcional)">
                <Select
                  searchable
                  options={[{ value: '', label: 'Sin desglose' }, ...dimensionOptions]}
                  value={draft.breakdown}
                  onChange={(breakdown) => set({ breakdown, ...(breakdown ? { secondMetric: null } : {}) })}
                  ariaLabel="Campo de desglose"
                />
              </Field>
            )}

            {!isKpi && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Ordenación">
                  <Select
                    options={SORT_OPTIONS}
                    value={draft.sort}
                    onChange={(sort) => set({ sort })}
                    ariaLabel="Ordenación de los grupos"
                  />
                </Field>
                <Field label="Máximo de grupos" hint="0 = mostrar todos los grupos.">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={draft.limit}
                    onChange={(event) => set({ limit: Number(event.target.value) })}
                    aria-label="Máximo de grupos"
                  />
                </Field>
              </div>
            )}

            {!isKpi && draft.limit > 0 && (
              <Switch
                checked={draft.groupOthers}
                onChange={(groupOthers) => set({ groupOthers })}
                label="Agrupar el resto de valores en “Otros”"
              />
            )}
          </div>
        )}

        {tab === 'formato' && (
          <div className="flex flex-col gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Título" htmlFor="widget-title">
                <Input
                  id="widget-title"
                  value={draft.title}
                  onChange={(event) => set({ title: event.target.value })}
                />
              </Field>
              <Field label="Subtítulo (opcional)" htmlFor="widget-subtitle">
                <Input
                  id="widget-subtitle"
                  value={draft.subtitle || ''}
                  onChange={(event) => set({ subtitle: event.target.value })}
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Ancho en la cuadrícula">
                <Segmented
                  size="md"
                  ariaLabel="Ancho del gráfico"
                  value={draft.span}
                  onChange={(span) => set({ span: Number(span) })}
                  options={SPAN_OPTIONS}
                />
              </Field>
              <Field label="Altura">
                <Segmented
                  size="md"
                  ariaLabel="Altura del gráfico"
                  value={draft.height}
                  onChange={(height) => set({ height })}
                  options={HEIGHT_OPTIONS}
                />
              </Field>
            </div>

            {!isKpi && (
              <Switch
                checked={draft.showLegend}
                onChange={(showLegend) => set({ showLegend })}
                label="Mostrar leyenda"
              />
            )}
          </div>
        )}

        {tab === 'filtros' && (
          <div className="flex flex-col gap-3">
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              Estos filtros se aplican <strong>solo a este gráfico</strong>, además de los filtros
              generales del informe.
            </p>
            <FilterBuilder
              compact
              rows={rows}
              group={draft.filters}
              onChange={(filters) => set({ filters })}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}
